'use client'

import React, { memo } from 'react'
import { useTranslations } from "next-intl";
import { CustomerAddressInput } from '@segway/core/src/graphql/generated/graphql'

import {
  CheckoutAddressValidationDialog,
  GuestPickupEmail,
  PaymentMethods,
} from '@/app/[locale]/checkout/_components/checkout'
import { Arrow } from '@/components'
import UserAddressForm from '@/components/userAddressForm/UserAddressForm'
import { Button, Form, FormControl, FormField, FormItem, FormMessage, Input } from '@/ui'

import usePickupPersonInformation from '../../../../../../hooks/checkout/usePickupPersonInformation'
import { PickupAddress } from '../pickupAddress'

/**
 * 结算页 Pickup/HomeDelivery 组件
 */
const PickupPersonInformation = () => {
  const {
    form,
    isPaymentStep,
    guestEmailFormRef,
    billingFormRef,
    shippingFormRef,
    handleNext,
    handleBack,
    billingValues,
    homeDeliveryShippingValues,
    paymentMethods,
    pickupValues,
    shippingVisible,
    pickupPersonInformationVisible,
    billingVisible,
    isRender,
    addressValidator,
    closeAddressValidator,
  } = usePickupPersonInformation()
  const tCommon = useTranslations('Common')

  return isRender ? (
    <div className="space-y-2 xl:space-y-[40px]">
      {isPaymentStep && (
        <span
          className="inline-flex cursor-pointer items-center justify-center xl:text-[16px]"
          onClick={handleBack}>
          <Arrow rotate={180} />
          {tCommon('back')}
        </span>
      )}

      {/* Pickup 表单 */}
      {pickupPersonInformationVisible && (
        <Form {...form}>
          <form className="rounded-[12px] bg-white px-3 py-4 xl:p-0">
            <p className="mb-3 text-[16px] xl:text-[20px]">Pickup Person Information</p>
            <div className="grid grid-cols-2 gap-2 xl:gap-3">
              <FormField
                control={form.control}
                name="firstname"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input label="First Name" required {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastname"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input label="Last Name" required {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 xl:mt-4 xl:gap-3">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input label="Email" required {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="telephone"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input label="Phone Number" required {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </form>
        </Form>
      )}

      {/* Home delivery shipping address 表单 */}
      <div
        className={`rounded-[12px] bg-white px-3 py-4 ${shippingVisible ? '' : 'hidden'} xl:p-0`}>
        <p className="mb-3 text-[16px] xl:text-[20px]">Shipping Address</p>
        <GuestPickupEmail ref={guestEmailFormRef} />
        <UserAddressForm
          ref={shippingFormRef}
          saveBtnVisible={false}
          defaultValues={homeDeliveryShippingValues}
        />
      </div>

      {/* Billing address 表单 */}
      {billingVisible && (
        <div className="rounded-[12px] bg-white px-3 py-4 xl:p-0">
          <p className="mb-3 text-[16px] xl:text-[20px]">Billing Address</p>
          <UserAddressForm
            ref={billingFormRef}
            saveBtnVisible={false}
            defaultValues={billingValues}
          />
        </div>
      )}

      <PickupAddress pickupPersonInformation={isPaymentStep ? pickupValues : null} />

      {!isPaymentStep && (
        <div className="fixed bottom-0 left-0 z-[9] w-full bg-white p-3 pt-5 xl:static xl:mt-[32px] xl:px-0">
          <Button
            onClick={handleNext}
            className="h-[45px] w-full rounded-full text-[16px] xl:h-[56px]">
            {tCommon('next')}
          </Button>
        </div>
      )}

      {isPaymentStep && paymentMethods && paymentMethods.length > 0 && (
        <PaymentMethods methods={paymentMethods} />
      )}

      <CheckoutAddressValidationDialog
        isOpen={addressValidator.modalVisible}
        address={addressValidator.address}
        improvedAddress={addressValidator.improvedAddress}
        addressComparison={addressValidator.addressComparison}
        errorMessage={addressValidator.errorMessage}
        title={addressValidator.title}
        onClose={closeAddressValidator}
        onSaveAddress={(address: CustomerAddressInput) => {
          closeAddressValidator()
          addressValidator?.saveCallback?.(address)
        }}
      />
    </div>
  ) : null
}

export default memo(PickupPersonInformation)
